import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function TokaiPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Tokai Painters Cape Town | Professional Painting Services | Mountain View Property Specialists";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Tokai properties. Expert residential painting, estate home services, interior & exterior painting. Serving Tokai Cape Town with premium quality finishes and mountain view property expertise.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Tokai painters, mountain view property painting, estate home painting, Tokai property painting, professional painters Cape Town');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/tokai`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Tokai Painters - Professional Painting Services Cape Town",
      "description": "Professional painting services for Tokai properties with mountain view expertise and estate home specialization.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/tokai`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Tokai",
        "description": "Specialized painting services for Tokai's mountain view properties and estate homes.",
        "areaServed": "Tokai, Cape Town",
        "serviceType": "Mountain View Property Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Tokai')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Estate Home Painting',
      description: 'Comprehensive painting services for Tokai\'s spacious estate homes and luxury properties, with attention to detail and premium finishes throughout.',
      price: 'From R15,000',
      features: ['Large-scale projects', 'Premium materials', 'Coordinated scheduling', 'Quality assurance']
    },
    {
      icon: 'ri-building-line',
      title: 'Mountain View Properties',
      description: 'Specialized painting for Tokai properties with mountain views, considering unique lighting conditions and environmental factors.',
      price: 'From R12,000',
      features: ['UV protection', 'Weather resistance', 'Color consultation', 'Scenic enhancement']
    },
    {
      icon: 'ri-brush-line',
      title: 'Interior Painting',
      description: 'Complete interior painting services for Tokai homes, from modern open-plan spaces to traditional room layouts, with expert color guidance.',
      price: 'From R8,000',
      features: ['Room preparation', 'Premium paints', 'Decorative finishes', 'Minimal disruption']
    },
    {
      icon: 'ri-palette-line',
      title: 'Exterior Painting',
      description: 'Professional exterior painting for Tokai properties, protecting against mountain weather while enhancing curb appeal and property value.',
      price: 'From R10,000',
      features: ['Weather protection', 'Surface preparation', 'Durable coatings', 'Color matching']
    },
    {
      icon: 'ri-shield-line',
      title: 'Protective Coatings',
      description: 'Advanced protective coatings for Tokai properties exposed to mountain weather conditions including wind, rain, and temperature variations.',
      price: 'From R9,000',
      features: ['Weatherproofing', 'Moisture barriers', 'UV resistance', 'Long-term protection']
    },
    {
      icon: 'ri-tools-line',
      title: 'Maintenance Programs',
      description: 'Regular maintenance painting services to protect your Tokai property investment with scheduled inspections and preventive care.',
      price: 'From R5,000',
      features: ['Scheduled visits', 'Preventive maintenance', 'Priority service', 'Detailed reports']
    }
  ];

  const testimonials = [
    {
      name: 'Michael Anderson',
      property: 'Mountain View Estate',
      rating: 5,
      text: 'Exceptional work on our Tokai estate home. The team handled the large-scale project professionally and the results are stunning. They understood our vision perfectly.',
      image: 'https://public.readdy.ai/ai/img_res/6a81f1ba7a41067d54db4158b1ddaeb9.jpg'
    },
    {
      name: 'Jennifer Clarke',
      property: 'Family Home',
      rating: 5,
      text: 'Outstanding service and quality. They transformed our Tokai home beautifully and worked efficiently around our family schedule. Highly recommend for any painting project.',
      image: 'https://readdy.ai/api/search-image?query=Happy%20woman%20homeowner%20in%20Tokai%20family%20home%20with%20mountain%20backdrop%2C%20professionally%20painted%20interior%2C%20satisfied%20client%20portrait&width=80&height=80&seq=tokai2&orientation=squarish'
    },
    {
      name: 'Robert van Zyl',
      property: 'Luxury Residence',
      rating: 5,
      text: 'Professional team with excellent attention to detail. The exterior painting has held up beautifully against Tokai\'s mountain weather conditions. Very pleased with the results.',
      image: 'https://readdy.ai/api/search-image?query=Distinguished%20man%20homeowner%20at%20Tokai%20luxury%20residence%20with%20mountain%20views%2C%20newly%20painted%20exterior%2C%20satisfied%20customer%20portrait&width=80&height=80&seq=tokai5&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Mountain Estate Renovation',
      description: 'Complete exterior and interior painting for large estate home with mountain views, including protective coatings and premium finishes',
      image: 'https://readdy.ai/api/search-image?query=Large%20estate%20home%20in%20Tokai%20with%20mountain%20backdrop%2C%20freshly%20painted%20exterior%2C%20luxury%20property%2C%20professional%20painting%20work&width=400&height=300&seq=tokai3&orientation=landscape'
    },
    {
      title: 'Modern Family Home',
      description: 'Contemporary interior and exterior painting with neutral palette and durable finishes for active family lifestyle',
      image: 'https://readdy.ai/api/search-image?query=Modern%20family%20home%20in%20Tokai%20with%20mountain%20views%2C%20newly%20painted%20exterior%2C%20contemporary%20architecture%2C%20professional%20finish&width=400&height=300&seq=tokai4&orientation=landscape'
    },
    {
      title: 'Luxury Residence Complex',
      description: 'Multi-property painting project for Tokai residential complex with coordinated colors and premium materials throughout',
      image: 'https://readdy.ai/api/search-image?query=Tokai%20residential%20complex%20with%20mountain%20setting%2C%20freshly%20painted%20exteriors%2C%20luxury%20homes%2C%20professional%20maintenance%20work&width=400&height=300&seq=tokai6&orientation=landscape'
    },
    {
      title: 'Heritage Home Restoration',
      description: 'Careful restoration and painting of older Tokai property preserving character while providing modern protection',
      image: 'https://readdy.ai/api/search-image?query=Heritage%20home%20in%20Tokai%20with%20mountain%20backdrop%2C%20restored%20painted%20exterior%2C%20period%20property%2C%20professional%20restoration%20work&width=400&height=300&seq=tokai7&orientation=landscape'
    }
  ];

  const paintingProcess = [
    {
      step: '1',
      title: 'Property Assessment',
      description: 'Comprehensive evaluation of your Tokai property including mountain exposure analysis, architectural style, and specific requirements.'
    },
    {
      step: '2',
      title: 'Custom Planning',
      description: 'Development of tailored painting solutions considering Tokai\'s mountain environment and your property\'s unique characteristics.'
    },
    {
      step: '3',
      title: 'Surface Preparation',
      description: 'Thorough preparation including cleaning, repairs, and priming to ensure optimal paint adhesion and long-lasting results.'
    },
    {
      step: '4',
      title: 'Professional Application',
      description: 'Expert application of weather-resistant paints using proven techniques for superior finish quality and durability.'
    },
    {
      step: '5',
      title: 'Quality Inspection',
      description: 'Detailed inspection and final touch-ups to ensure every aspect meets our high standards and your expectations.'
    },
    {
      step: '6',
      title: 'Maintenance Guidance',
      description: 'Comprehensive care instructions and maintenance recommendations specific to your Tokai property\'s needs and location.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20estate%20home%20in%20Tokai%20with%20mountain%20views%2C%20painting%20equipment%2C%20scaffolding%2C%20beautiful%20property%20with%20mountain%20backdrop%2C%20sunny%20day&width=1920&height=1080&seq=tokaihero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Tokai
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Tokai estate homes and mountain view properties. Specialized finishes and weather-resistant coatings for your property with protection against mountain environmental conditions and premium quality results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Tokai Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Premium Painting Services in Tokai
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Tokai is one of Cape Town's most desirable residential areas, nestled against the eastern slopes of the Constantiaberg mountains. This established suburb is renowned for its spacious properties, magnificent mountain views, and proximity to the Tokai Forest and vineyards. The area attracts families and professionals who appreciate the perfect combination of natural beauty, tranquility, and convenient access to excellent schools, shopping centers, and major transport routes.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The unique location of Tokai presents specific considerations for property maintenance and painting services. Properties in this area are exposed to mountain weather conditions including variable temperatures, occasional strong winds, and higher rainfall compared to coastal areas. The combination of these environmental factors, along with the area's lush vegetation and proximity to the forest, requires specialized painting solutions that provide superior protection while maintaining aesthetic appeal.
              </p>
              <p className="text-lg text-gray-600">
                Our team has extensive experience working with Tokai's diverse property types, from large estate homes on expansive plots to modern family residences and townhouse complexes. We understand the specific requirements of mountain-facing properties and provide tailored solutions that ensure long-lasting protection and beautiful finishes. Whether your property features traditional architecture or contemporary design, we deliver results that enhance its value and complement the stunning natural surroundings.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Tokai%20Cape%20Town%20residential%20area%20with%20mountain%20views%2C%20estate%20homes%2C%20lush%20vegetation%2C%20beautiful%20properties%20against%20mountain%20backdrop&width=600&height=400&seq=tokai8&orientation=landscape"
                alt="Tokai Residential Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Tokai Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions designed specifically for Tokai's mountain environment and diverse property types.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Tokai Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results for your Tokai mountain view property.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Tokai */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Tokai?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in Tokai's unique mountain environment with proven results and satisfied customers.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-mountain-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Mountain Expertise</h3>
              <p className="text-gray-600">Specialized knowledge of mountain weather conditions and their impact on paint performance and longevity in Tokai properties.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-home-4-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Estate Home Specialists</h3>
              <p className="text-gray-600">Extensive experience with large-scale estate home projects, managing complex requirements and delivering premium results.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Weather Protection</h3>
              <p className="text-gray-600">Advanced protective coatings specifically selected for Tokai\'s mountain climate, ensuring long-lasting performance and beauty.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-customer-service-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Premium Service</h3>
              <p className="text-gray-600">Dedicated project management and customer service ensuring smooth execution and complete satisfaction for all Tokai projects.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Tokai Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in Tokai estate home and residential painting with recent successful projects across the area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tokai Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Tokai property owners who have experienced our professional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mountain Environment Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Understanding Tokai's Mountain Environment
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Environmental Factors We Address</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-temp-cold-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Temperature Variations</h4>
                    <p className="text-gray-600">Mountain locations experience greater temperature fluctuations requiring flexible coatings that can expand and contract without cracking.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-rainy-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Higher Rainfall</h4>
                    <p className="text-gray-600">Tokai receives more rainfall than coastal areas, requiring superior moisture resistance and waterproofing solutions for lasting protection.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-plant-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Vegetation Impact</h4>
                    <p className="text-gray-600">Proximity to forest and lush vegetation increases moisture levels and organic growth, requiring specialized anti-fungal treatments.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-sun-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">UV Exposure</h4>
                    <p className="text-gray-600">Mountain properties receive intense UV radiation requiring specialized UV-resistant formulations to prevent color fading and surface degradation.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Tokai%20mountain%20environment%20with%20lush%20vegetation%2C%20forest%20proximity%2C%20mountain%20weather%20conditions%2C%20residential%20properties%20in%20natural%20setting&width=600&height=500&seq=tokai9&orientation=portrait"
                alt="Tokai Mountain Environment"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Tokai Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's mountain property painting specialists for Tokai. Our expert team understands the unique requirements of mountain-facing properties and is ready to provide you with a comprehensive consultation and detailed quote. Experience the difference that specialized mountain environment painting expertise makes for your Tokai estate home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
